import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Package, TriangleAlert, Sprout, ChevronDown, SlidersHorizontal, Download, Check, Loader2, Lock } from 'lucide-react';
import { convertWeight, weightUnitLabel, weightUnitOptionLabel, DEFAULT_BOX_KG } from '../../../utils/groupByVarietyType';
import { formatCompactCount } from '../../../utils/compactCount';
import { formatCompactTonnes } from '../../../utils/formatCompactTonnes';
import { resolveSeasonCommodity } from '../../../constants/importerSeasonPdf';
import { mintImporterReportDownload } from '../../../utils/api';
import { pdfLangFromI18n } from '../../../constants/importerSeasonPdf';
import {
  showSeasonSummaryChrome,
  showSeasonVarietyCards,
} from '../../../utils/seasonAccessGate';
import { resolveSeasonDestKey } from '../../../utils/seasonDestination';
import {
  mergeSeasonLivePack,
  resolveSeasonChromeState,
  seasonPdfDisabledReason,
  selectSeasonFromActive,
} from '../../../utils/seasonFromActive';
import useSeasonLivePack from '../../../hooks/useSeasonLivePack';
import TopImportersModal from './TopImportersModal';
import SeasonSummaryModal from './SeasonSummaryModal';
import MapSelect from './MapSelect';
import useDownloadFeedback from '../../../hooks/useDownloadFeedback';
import { triggerPdfDownload } from '../../../utils/triggerPdfDownload';
import { createDownloadGuard } from '../../../utils/downloadGuard';
import { snapshotLoadingText } from '../../../utils/seasonLoadingText';

/**
 * Summary header of the Global Produce Map card: a Shipments column (arrival
 * windows + the weight-unit toggle) and, past a vertical divider, an Alerts
 * column. Every tile stays a button — clicking one opens the matching
 * MapSidebar panel, same contract as before the restyle.
 */

// Season access levels that may request a live (non pre-generated) summary.
const LIVE_SEASON_ACCESS = ['metrics', 'importers', 'exporters', 'all'];

const SECTION_TITLE_CLASS =
  "font-['Inter'] text-[14px] font-semibold leading-[21px] text-[#282A29] dark:text-[#f0f5f2]";

/**
 * Desktop section header: never wraps, fixed minimum band so the Shipments one
 * (which carries the unit control) and the others sit on the same line and
 * their tiles start at the same height.
 */
const SECTION_HEADER_CLASS = 'flex flex-nowrap items-center gap-x-[6px] h-[36px] shrink-0';

// Season summary icon: table frame and header row in the brand green, inner
// grid lines in a lighter tint of the same green.
const SeasonTableIcon = ({ size = 18, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M9 8.5V19M15 8.5V19M3 13.75h18" stroke="currentColor" strokeOpacity="0.4" strokeLinecap="butt" />
    <rect x="3" y="4" width="18" height="15" rx="2.5" stroke="currentColor" />
    <path d="M3 8.5h18" stroke="currentColor" />
  </svg>
);

// Vertical rule between two desktop sections of the one-line bar.
const SectionDivider = () => (
  <div
    data-testid="section-divider"
    aria-hidden="true"
    className="w-px shrink-0 self-stretch bg-[#E8EBE9] dark:bg-[#30363d]"
  />
);

// Desktop (lg+) only — mobile/portrait (<lg) renders MobileChipCard instead
// (see below). Cards fill their grid cell; equal height comes from the grid's
// stretch + the min-height. `fit` (Shipments) keeps both lines on one line at
// full width instead of ellipsizing — the grid sizes the cell to the content.
const StatCard = ({ label, value, title, isActive, onClick, fit = false }) => {
  const clip = fit ? 'whitespace-nowrap' : 'truncate';
  const className = `flex w-full min-w-0 flex-col justify-center gap-[2px] rounded-[8px] border px-[8px] py-[8px] text-left transition-colors lg:min-h-[55px] ${
    isActive
      ? 'border-bloom-green bg-[#F0F9F4] dark:border-bloom-green dark:bg-[#173322]'
      : 'border-[#E8EBE9] bg-white dark:border-[#30363d] dark:bg-[#1d1d1d]'
  } ${onClick ? `cursor-pointer ${isActive ? '' : 'hover:border-bloom-green/60'}` : ''}`;
  const body = (
    <>
      <span title={label} className={`font-['Inter'] ${clip} text-[10px] font-medium uppercase leading-[15px] tracking-[0.06em] text-[#828382]`}>
        {label}
      </span>
      <span className={`font-['Inter'] ${clip} text-[13px] font-semibold leading-[20px] text-[#282A29] dark:text-[#f0f5f2]`}>
        {value}
      </span>
    </>
  );
  if (!onClick) {
    return <div title={title || value} className={className}>{body}</div>;
  }
  return (
    <button type="button" onClick={onClick} title={title || value} className={className}>
      {body}
    </button>
  );
};

// Number-less placeholder with the StatCard footprint (compact = MobileChipCard footprint).
const SkeletonCard = ({ compact = false }) => (
  <div
    aria-hidden="true"
    className={`skeleton-sheen flex w-full min-w-0 flex-col justify-center gap-[6px] rounded-[8px] ${
      compact ? 'min-h-11 flex-1 px-2 py-1.5' : 'px-[8px] py-[8px] lg:min-h-[55px]'
    }`}
  >
    <span className="h-[8px] w-2/3 rounded bg-[#C5D0CA] dark:bg-[#3a403c]" />
    <span className="h-[10px] w-1/2 rounded bg-[#C5D0CA] dark:bg-[#3a403c]" />
  </div>
);

// Placeholder for a number the backend withheld; drawn locally, never real data.
const LockedValue = () => (
  <span aria-hidden="true" className="select-none blur-[5px]">00% · 0,000 tn</span>
);

// Disabled Download shown to locked (teaser) users.
const LockedDownload = ({ label, hint, className }) => (
  <button type="button" disabled title={hint} aria-label={`${label}: ${hint}`} className={className}>
    <Lock size={12} className="shrink-0" />
    {label}
  </button>
);

const UNITS = ['boxes', 'kg', 'lbs'];

/**
 * Label for one entry of the unit picker. `boxes` carries the box size here
 * ("boxes 8.2kg") so it is stated exactly once on the card — the per-value
 * labels next to each number stay short (see weightUnitLabel), which is what
 * keeps the tile values readable instead of truncated.
 */
const unitOptionLabel = (t, u) =>
  (u === 'boxes'
    ? t('map.scorecards.boxesWithWeight', {
        boxKg: DEFAULT_BOX_KG,
        defaultValue: weightUnitOptionLabel('boxes'),
      })
    : t(`map.scorecards.${u}`, u));

const WeightUnitToggle = ({ weightUnit, onWeightUnitChange }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-[2px] rounded-[8px] bg-[#F0F4F1] p-[2px] dark:bg-[#282a29]">
      {UNITS.map((u) => (
        <button
          key={u}
          type="button"
          onClick={() => onWeightUnitChange(u)}
          className={`font-['Inter'] cursor-pointer rounded-[6px] px-[8px] py-[3px] text-[11px] font-medium transition-colors ${
            weightUnit === u
              ? 'bg-[#DCFCE7] text-[#12823F] dark:bg-[#173322] dark:text-[#7EE6A4]'
              : 'text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2]'
          }`}
        >
          {unitOptionLabel(t, u)}
        </button>
      ))}
    </div>
  );
};

// Mobile/portrait (<lg) compact chip cards — ported verbatim (layout) from
// feat/portrait-signage's ScorecardsBar: an optional leading icon plus a
// 2-line label/value stack, flex-1 so cards share row width evenly instead
// of the desktop's fixed-width sectioned StatCard. `icon` is optional — the
// arrivals chips and the Weather Alerts chip render without one (the
// per-chip Ship/CloudLightning icons were dropped as redundant next to the
// section header's own icon); Harvest Alerts keeps its Sprout icon. Desktop
// (lg+) below is untouched — same StatCard/section markup as before.
const MobileChipCard = ({ icon: Icon, label, compact, isActive, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex min-h-11 flex-1 min-w-0 items-center gap-1.5 rounded-lg border px-2 py-1.5 text-left transition-colors ${
      isActive
        ? 'border-bloom-green bg-bloom-green text-white'
        : 'border-[#E8EBE9] bg-white text-[#282A29] hover:border-bloom-green/50 dark:border-[#30363d] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]'
    }`}
  >
    {Icon && <Icon size={14} className="shrink-0" />}
    <div className="min-w-0 text-left">
      <div title={typeof label === 'string' ? label : undefined} className={`whitespace-normal break-words text-[11px] leading-tight ${isActive ? 'text-white/80' : 'text-[#828382] dark:text-gray-500'}`}>
        {label}
      </div>
      {/* Wraps instead of truncating — these chips are narrow and the value is
          the reason the chip exists, so a second line beats an ellipsis. */}
      <div className="whitespace-normal break-words text-xs font-semibold leading-tight" title={typeof compact === 'string' ? compact : undefined}>{compact}</div>
    </div>
  </button>
);

const SectionPill = ({ icon: Icon, iconClass, label, expanded, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={expanded}
    className={`flex min-h-11 items-center justify-center gap-1.5 rounded-lg border px-3 text-xs font-medium whitespace-nowrap transition-colors ${
      expanded
        ? 'border-bloom-green bg-[#F0F9F4] text-[#282A29] dark:border-bloom-green dark:bg-[#173322] dark:text-[#f0f5f2]'
        : 'border-[#C3C4C3] bg-white text-[#282A29] dark:border-[#3a3a3a] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]'
    }`}
  >
    {Icon && <Icon size={14} className={`shrink-0 ${iconClass}`} />}
    <span>{label}</span>
    <ChevronDown size={14} className={`shrink-0 text-[#828382] transition-transform ${expanded ? 'rotate-180' : ''}`} />
  </button>
);

const ScorecardsBar = ({
  arriving3Count, arriving3Weight,
  arriving7Count, arriving7Weight,
  arriving10Count, arriving10Weight,
  alertsCount, harvestAlertsCount = 0, activePanel, onPanelToggle,
  weightUnit = 'boxes', onWeightUnitChange,
  expandedSection = null, onExpandedSectionToggle = () => {},
  // Server top_importers once the active response carries it; null → keep Alerts (hide).
  topImporters = null,
  // Season summary PDF Download: map_season_access importers|exporters|all (`all` only).
  canDownloadSeasonPdf = false,
  selectedCommodity = '',
  // Key Season Summary state to signed-in email (Astra MUST-FIX account switch).
  email = '',
  // Map ?dest= / ?origin= (Ask #51/#55). No USA soft-default.
  destSel = null,
  originSel = null,
  // Season packs from the importer_report_preview bundle=season request.
  seasonBundle = null,
  // True while the season bundle request is loading.
  seasonBundleLoading = false,
  // True when the season bundle request failed (no season data to show).
  seasonBundleError = false,
}) => {
  const { t, i18n } = useTranslation();
  const showTopImporters = Array.isArray(topImporters);
  const feedback = useDownloadFeedback();
  const pdfBusy = feedback.phase !== 'idle';
  const [pdfErr, setPdfErr] = useState('');
  const [pdfErrIsInfo, setPdfErrIsInfo] = useState(false);
  const downloadGuard = useRef(createDownloadGuard()).current;
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [seasonSummaryOpen, setSeasonSummaryOpen] = useState(false);
  // Variety % cards are always for the selected commodity only (Sekoya≠Murcott).
  const seasonCommodity = resolveSeasonCommodity(selectedCommodity);
  const seasonDest = resolveSeasonDestKey(destSel);
  // A cell with no pre-generated pack is aggregated live by the backend. The
  // scope comes from the unmerged selection so the hook does not depend on its own output.
  const baseSelected = selectSeasonFromActive(seasonBundle, destSel, seasonCommodity, originSel);
  const live = useSeasonLivePack({
    scope: baseSelected.scope,
    commodity: seasonCommodity,
    email,
    enabled:
      baseSelected.destMissing &&
      Boolean(baseSelected.scope) &&
      seasonDest.fetchable &&
      !baseSelected.originMissing &&
      (seasonBundle?.status === 'ready' || seasonBundle?.status === 'metrics') &&
      LIVE_SEASON_ACCESS.includes(baseSelected.seasonAccess),
  });
  const mergedBundle = mergeSeasonLivePack(seasonBundle, baseSelected.scope, seasonCommodity, live.pack);
  const selected = selectSeasonFromActive(mergedBundle, destSel, seasonCommodity, originSel);
  const seasonState = resolveSeasonChromeState({
    seasonBundle: mergedBundle,
    seasonBundleLoading,
    seasonBundleError,
    selected,
    destKey: seasonDest.key,
    liveStatus: live.status,
  });
  const seasonLoading = seasonState === 'loading';
  const seasonUnavailable = seasonState === 'unavailable';
  const seasonErrored = seasonState === 'error';
  // A live loading/error keeps the access level (the bundle itself is fine) so the modal can still say why.
  const seasonAccess =
    (seasonLoading || seasonUnavailable || seasonErrored) && live.status === 'idle'
      ? null
      : selected.seasonAccess;
  const seasonDestMissing = selected.destMissing || selected.originMissing;
  const topVarieties = showSeasonVarietyCards(seasonAccess, selected.pack)
    ? (Array.isArray(selected.pack?.varieties) ? selected.pack.varieties.slice(0, 3) : [])
    : [];
  // Season crops always use Season Summary chrome (never fall back to Top
  // Importers / on-water Alloy). ACL still gates cards vs empty shell.
  const isSeasonCrop = Boolean(seasonCommodity);
  const showSeasonSummary = isSeasonCrop;
  const seasonChromeAllowed =
    seasonAccess != null && showSeasonSummaryChrome(seasonAccess);
  // Unsupported destination/origin: the one-line message stands alone, no dead buttons.
  const seasonUnsupported =
    seasonState === 'dest_unsupported' || seasonState === 'origin_unsupported';
  const pdfDisabledReason = seasonPdfDisabledReason(
    seasonState,
    seasonDest.key,
    selected.pack,
  );
  const pdfDisabledTitle =
    pdfDisabledReason === 'seasonSelectDestination'
      ? t('map.scorecards.seasonSelectDestination', 'Select a destination to see the season summary.')
      : pdfDisabledReason === 'seasonOriginNotPublished'
          ? t(
              'map.scorecards.seasonOriginNotPublished',
              'Season summary is published for Peru, Chile or both. Select Peru and/or Chile as the origin.',
            )
          : pdfDisabledReason === 'seasonScopeNotPublished'
            ? t('map.scorecards.seasonScopeNotPublished', 'Season report not published for this combination yet.')
            : pdfDisabledReason === 'seasonEmpty'
              ? t('map.scorecards.reportEmpty', 'No shipments for this combination in the season window')
              : pdfDisabledReason === 'pdfNotPublished'
                ? t('map.scorecards.pdfNotPublished', 'Season PDF not published yet for this selection')
                : undefined;

  // Close modal when commodity / dest / account changes.
  useEffect(() => {
    setSeasonSummaryOpen(false);
  }, [seasonCommodity, email, seasonDest.key]);

  // The PDF notice describes one selection: clear it (and drop any in-flight response) when that changes.
  useEffect(() => {
    downloadGuard.bump();
    setPdfErr('');
    setPdfErrIsInfo(false);
    feedback.reset();
  }, [seasonCommodity, seasonDest.key, selected.originKey, selected.scope, email, i18n?.language, canDownloadSeasonPdf]);

  const onDownloadReport = async () => {
    if (
      !canDownloadSeasonPdf ||
      !seasonCommodity ||
      !seasonDest.fetchable ||
      seasonDestMissing ||
      pdfDisabledReason ||
      seasonState !== 'ready'
    ) {
      return;
    }
    const fbGen = feedback.start();
    if (fbGen == null) return;
    const gen = downloadGuard.begin();
    setPdfErr('');
    setPdfErrIsInfo(false);
    try {
      const body = await mintImporterReportDownload({
        destination: selected.scope,
        commodity: seasonCommodity,
        kind: 'season_to_date',
        lang: pdfLangFromI18n(i18n),
      });
      if (!downloadGuard.isCurrent(gen)) {
        return;
      }
      if (triggerPdfDownload(body.url)) feedback.handoff(fbGen);
      if (body?.lang_fallback && downloadGuard.isCurrent(gen)) {
        setPdfErr(t('map.scorecards.pdfLangFallback', 'Spanish PDF not available yet. The English version was downloaded.'));
        setPdfErrIsInfo(true);
      }
    } catch (e) {
      if (downloadGuard.isCurrent(gen)) {
        setPdfErr(e?.message || t('map.scorecards.pdfError', 'Report unavailable'));
        setPdfErrIsInfo(false);
      }
    } finally {
      feedback.fail(fbGen);
    }
  };

  // Single text row shown instead of variety cards (desktop and mobile share it).
  const seasonMessage = (() => {
    if (seasonLoading) {
      return live.status === 'loading'
        ? snapshotLoadingText(t)
        : t('map.scorecards.loadingVarieties', 'Loading varieties…');
    }
    if (seasonErrored) return t('map.scorecards.seasonError', 'Season summary could not be loaded.');
    if (seasonUnavailable) {
      return t(
        'map.scorecards.seasonUnavailable',
        'Season summary is temporarily unavailable. Refresh the map to try again.',
      );
    }
    if (seasonState === 'empty') {
      return t('map.scorecards.reportEmpty', 'No shipments for this combination in the season window');
    }
    if (seasonState === 'hidden') {
      return t('map.scorecards.seasonAccessDenied', 'Season summary not available for this account');
    }
    if (seasonState === 'metrics') {
      return t('map.scorecards.seasonMetricsEmpty', 'Season metrics coming soon');
    }
    if (seasonDestMissing || topVarieties.length === 0) {
      if (selected.originMissing) {
        return t(
          'map.scorecards.seasonOriginNotPublished',
          'Season summary is published for Peru, Chile or both. Select Peru and/or Chile as the origin.',
        );
      }
      if (seasonDest.key === 'NONE') {
        return t('map.scorecards.seasonSelectDestination', 'Select a destination to see the season summary.');
      }
      return t('map.scorecards.seasonScopeNotPublished', 'Season report not published for this combination yet.');
    }
    return null;
  })();
  const varietyLabel = (v, i) => `#${i + 1} ${v.name || '—'}`;
  const seasonLocked = seasonState === 'teaser';
  const lockedHint = t('map.scorecards.seasonLockedHint', 'Elite plan');
  const renderUpgradeLine = (cls) =>
    seasonLocked && !seasonUnsupported ? (
      <div data-testid="season-locked-upgrade" className={cls}>
        {t('map.scorecards.lockedBenefit', 'Unlock the full season summary with the Elite plan')}{' '}
        <Link to="/billing" className="font-semibold text-bloom-green hover:underline">
          {t('map.scorecards.upgradeElite', 'Upgrade to Elite')}
        </Link>
      </div>
    ) : null;
  const varietyValue = (v) =>
    seasonLocked
      ? <LockedValue />
      : `${Number(v.share || 0).toFixed(0)}% · ${formatCompactTonnes(v.tonnes)} tn`;
  const varietyKey = (v, i) => `${seasonCommodity}:${seasonDest.key}:${i}:${v.name || 'variety'}`;
  const downloadDisabled = seasonState !== 'ready' || Boolean(pdfDisabledReason);
  const downloadLongLabel = t('map.scorecards.downloadSeasonSummary', 'Download season summary');
  const downloadShortLabel = t('map.scorecards.download', 'Download');
  const downloadText =
    feedback.phase === 'preparing'
      ? t('map.scorecards.pdfBusy', 'Downloading…')
      : feedback.phase === 'handoff'
        ? t('map.scorecards.pdfDownloading', 'Downloading…')
        : downloadShortLabel;
  const downloadIcon =
    feedback.phase === 'preparing' ? (
      <Loader2 size={14} aria-hidden="true" className="shrink-0 animate-spin text-bloom-green motion-reduce:animate-none" />
    ) : feedback.phase === 'handoff' ? (
      <Check size={14} aria-hidden="true" className="shrink-0 text-bloom-green" />
    ) : (
      <Download size={14} aria-hidden="true" className="shrink-0 text-bloom-green" />
    );
  const downloadStatus = (
    <span role="status" aria-live="polite" className="sr-only">
      {pdfBusy ? downloadText : ''}
    </span>
  );

  const shipLabel = t('map.scorecards.shipments', 'shipments');
  const fmtWeight = (w) => `${convertWeight(w, weightUnit).toLocaleString()} ${weightUnitLabel(weightUnit)}`;
  // Desktop tiles: compact count ("1.39M boxes"); the precise value stays in the title.
  const fmtWeightCompact = (w) => `${formatCompactCount(convertWeight(w, weightUnit), i18n?.language)} ${weightUnitLabel(weightUnit)}`;
  // Mobile compact value line for the alert cards — "N Active alert(s)" reads
  // clearer than the bare count in the cramped chip width.
  const activeAlertsLabel = (n) =>
    `${n} ${n === 1 ? t('map.scorecards.activeAlert', 'Active alert') : t('map.scorecards.activeAlerts', 'Active alerts')}`;

  const shipmentCards = [
    {
      id: 'arrivals3',
      label: t('map.scorecards.arriving3', 'Arriving in 3 days'),
      value: fmtWeight(arriving3Weight),
      title: `${arriving3Count} ${shipLabel} · ${fmtWeight(arriving3Weight)}`,
    },
    {
      id: 'arrivals',
      label: t('map.scorecards.arriving', 'Arriving in 7 days'),
      value: fmtWeight(arriving7Weight),
      title: `${arriving7Count} ${shipLabel} · ${fmtWeight(arriving7Weight)}`,
    },
    {
      id: 'arrivals10',
      label: t('map.scorecards.arriving10', 'Arriving in 10 days'),
      value: fmtWeight(arriving10Weight),
      title: `${arriving10Count} ${shipLabel} · ${fmtWeight(arriving10Weight)}`,
    },
  ];

  const alertCards = [
    {
      id: 'alerts',
      label: t('map.scorecards.weatherAlerts', 'Weather Alerts'),
      value: `${alertsCount} ${t('map.scorecards.active', 'active')}`,
    },
    // Only surface the Harvest Alerts card when there is at least one active alert.
    ...(harvestAlertsCount > 0
      ? [{
          id: 'harvest',
          label: t('map.scorecards.harvestAlerts', 'Harvest Alerts'),
          value: `${harvestAlertsCount} ${t('map.scorecards.active', 'active')}`,
        }]
      : []),
  ];

  const renderCard = (card, fit = false) => (
    <StatCard
      key={card.id}
      fit={fit}
      label={card.label}
      value={card.value}
      title={card.title}
      isActive={activePanel === card.id}
      onClick={() => onPanelToggle(activePanel === card.id ? null : card.id)}
    />
  );

  // Mobile chip cards — same ids/labels as the desktop cards above. Arrivals
  // show only the weight in the selected unit (boxes / kg / lbs); alerts show
  // "N Active alert(s)".
  const mobileArrivalCards = [
    { id: 'arrivals3', label: t('map.scorecards.arriving3', 'Arriving in 3 days'), compact: fmtWeight(arriving3Weight) },
    { id: 'arrivals', label: t('map.scorecards.arriving', 'Arriving in 7 days'), compact: fmtWeight(arriving7Weight) },
    { id: 'arrivals10', label: t('map.scorecards.arriving10', 'Arriving in 10 days'), compact: fmtWeight(arriving10Weight) },
  ];
  const mobileAlertCards = [
    { id: 'alerts', label: t('map.scorecards.weatherAlerts', 'Weather Alerts'), compact: activeAlertsLabel(alertsCount) },
    ...(harvestAlertsCount > 0
      ? [{ id: 'harvest', icon: Sprout, label: t('map.scorecards.harvestAlerts', 'Harvest Alerts'), compact: activeAlertsLabel(harvestAlertsCount) }]
      : []),
  ];
  const renderMobileCard = (card) => (
    <MobileChipCard
      key={card.id}
      icon={card.icon}
      label={card.label}
      compact={card.compact}
      isActive={activePanel === card.id}
      onClick={() => onPanelToggle(activePanel === card.id ? null : card.id)}
    />
  );

  const alertsSection = (
    <div className={`flex flex-col gap-[8px] ${harvestAlertsCount > 0 ? 'flex-[0_0_246px]' : 'flex-[0_0_120px]'}`}>
      <div className={SECTION_HEADER_CLASS}>
        <TriangleAlert size={18} className="shrink-0 text-[#EF4444]" />
        <span className={`shrink-0 whitespace-nowrap ${SECTION_TITLE_CLASS}`}>
          {t('map.scorecards.alertsTitle', 'Alerts')}
        </span>
      </div>
      <div className={`grid items-stretch gap-[6px] ${alertCards.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {alertCards.map((card) => renderCard(card))}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile/portrait (<lg): Option B collapsible chrome — a row of
          tappable pills (Shipments/Alerts/Map controls, one-open-at-a-time
          via `expandedSection` lifted to MapPage) replaces the old always-on
          two-row chip layout, so the map is the hero and this bar collapses
          to a single row by default. */}
      <div className="relative isolate z-[1250] flex flex-col gap-1.5 border-b border-[#E8EBE9] px-[12px] py-[10px] dark:border-[#30363d] lg:hidden">
        <div className="flex flex-wrap items-stretch gap-2">
          <SectionPill icon={Package} iconClass="text-[#B45309]" label={t('map.scorecards.shipmentsTitle', 'Shipments')} expanded={expandedSection === 'shipments'} onClick={() => onExpandedSectionToggle('shipments')} />
          <SectionPill icon={TriangleAlert} iconClass="text-[#EF4444]" label={t('map.scorecards.alertsTitle', 'Alerts')} expanded={expandedSection === 'alerts'} onClick={() => onExpandedSectionToggle('alerts')} />
          {showSeasonSummary && (
            <SectionPill icon={SeasonTableIcon} iconClass="text-bloom-green" label={t('map.scorecards.seasonSummary', 'Season summary')} expanded={expandedSection === 'season'} onClick={() => onExpandedSectionToggle('season')} />
          )}
          <SectionPill icon={SlidersHorizontal} iconClass="text-[#5d625f] dark:text-[#9aa19d]" label={t('map.controls.label', 'Map controls')} expanded={expandedSection === 'controls'} onClick={() => onExpandedSectionToggle('controls')} />
        </div>
        {expandedSection === 'shipments' && (
          <>
            <div className="flex items-stretch gap-2">{mobileArrivalCards.map(renderMobileCard)}</div>
            <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
              <span className="text-xs text-[#828382] dark:text-gray-500">{t('map.scorecards.units', 'Units')}:</span>
              <MapSelect value={weightUnit} options={UNITS.map((u) => ({ value: u, label: unitOptionLabel(t, u) }))} onSelect={onWeightUnitChange} ariaLabel={t('map.scorecards.weightUnit', 'Weight unit')} variant="inline" />
            </div>
          </>
        )}
        {expandedSection === 'alerts' && (
          <div className="flex items-stretch gap-2">{mobileAlertCards.map(renderMobileCard)}</div>
        )}
        {showSeasonSummary && expandedSection === 'season' && (
          <div data-testid="season-strip-mobile" className="flex flex-col gap-1.5">
            {seasonLoading ? (
              <div data-testid="season-cards" aria-busy="true" className="flex items-stretch gap-2">
                {[0, 1, 2].map((i) => <SkeletonCard key={i} compact />)}
              </div>
            ) : null}
            {seasonMessage ? (
              <div data-testid="season-message" role={seasonLoading ? 'status' : undefined} className={`break-words text-xs text-[#828382] dark:text-gray-500${seasonLoading ? ' text-center' : ''}`}>{seasonMessage}</div>
            ) : (
              <div data-testid="season-cards" className="flex items-stretch gap-2">
                {topVarieties.map((v, i) => (
                  <MobileChipCard
                    key={varietyKey(v, i)}
                    label={varietyLabel(v, i)}
                    compact={varietyValue(v)}
                    isActive={false}
                    onClick={() => setSeasonSummaryOpen(true)}
                  />
                ))}
              </div>
            )}
            {(seasonChromeAllowed || canDownloadSeasonPdf || seasonLocked) && !seasonUnsupported && (
              <div className="flex flex-wrap gap-2">
                {seasonChromeAllowed ? (
                  <button
                    type="button"
                    data-testid="season-view-all"
                    onClick={() => setSeasonSummaryOpen(true)}
                    className="flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#C3C4C3] bg-white px-3 text-xs font-medium whitespace-nowrap text-[#282A29] dark:border-[#3a3a3a] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]"
                  >
                    <ChevronDown size={14} className="shrink-0 text-bloom-green" />
                    {t('map.scorecards.viewAll', 'View all')}
                  </button>
                ) : null}
                {seasonLocked ? (
                  <LockedDownload
                    label={downloadShortLabel}
                    hint={lockedHint}
                    className="flex min-h-11 min-w-[112px] flex-1 cursor-not-allowed items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#C3C4C3] bg-white px-3 text-xs font-medium text-[#282A29] opacity-50 dark:border-[#3a3a3a] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]"
                  />
                ) : null}
                {canDownloadSeasonPdf ? downloadStatus : null}
                {canDownloadSeasonPdf ? (
                  <button
                    type="button"
                    data-testid="season-download"
                    onClick={onDownloadReport}
                    disabled={downloadDisabled}
                    aria-disabled={pdfBusy || undefined}
                    aria-label={downloadLongLabel}
                    className="flex min-h-11 min-w-[112px] flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#C3C4C3] bg-white px-3 text-xs font-medium whitespace-nowrap text-[#282A29] disabled:opacity-50 dark:border-[#3a3a3a] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]"
                  >
                    {downloadIcon}
                    {downloadText}
                  </button>
                ) : null}
              </div>
            )}
            {renderUpgradeLine('break-words text-xs text-[#5d625f] dark:text-[#a8b0ab]')}
            {canDownloadSeasonPdf && pdfDisabledTitle && pdfDisabledTitle !== seasonMessage ? (
              <div data-testid="season-download-reason" className="break-words text-xs text-[#828382] dark:text-gray-500">{pdfDisabledTitle}</div>
            ) : null}
            {pdfErr ? (
              <div role={pdfErrIsInfo ? 'status' : undefined} className={`break-words text-xs ${pdfErrIsInfo ? 'text-[#6B7280] dark:text-gray-400' : 'text-[#EF4444]'}`}>{pdfErr}</div>
            ) : null}
          </div>
        )}
      </div>

      {/* Desktop (lg+): sectioned Shipments / Season summary / Alerts bar on ONE
          line. The outer element scrolls horizontally when narrower than the
          row's minimum width instead of wrapping. Shipments tiles are sized to
          their content (one shared width, no truncation); content keeps its
          size, everything is left-aligned and spare width stays empty on the right.
          `relative isolate z-[1250]` elevates this bar's own stacking context
          above the Leaflet map sibling below (map is `z-0 isolate`).
          Untouched below lg — the `lg:hidden` block above owns mobile. */}
      <div data-testid="scorecards-bar" className="relative isolate z-[1250] hidden overflow-x-auto border-b border-[#E8EBE9] dark:border-[#30363d] lg:block">
        <div className={`flex w-full flex-nowrap items-stretch justify-start gap-x-[16px] px-[12px] py-[12px] ${showSeasonSummary ? (harvestAlertsCount > 0 ? 'min-w-[1260px]' : 'min-w-[1134px]') : 'min-w-[862px]'}`}>
          {/* Shipments */}
          <div className="flex w-max flex-none flex-col gap-[8px]">
            <div className={SECTION_HEADER_CLASS}>
              <Package size={18} className="shrink-0 text-[#B45309]" />
              <span className={`shrink-0 whitespace-nowrap ${SECTION_TITLE_CLASS}`}>
                {t('map.scorecards.shipmentsTitle', 'Shipments')}
              </span>
              <WeightUnitToggle weightUnit={weightUnit} onWeightUnitChange={onWeightUnitChange} />
            </div>
            <div className="grid w-max grid-cols-3 items-stretch gap-[6px]">
              {shipmentCards.map((card) => renderCard(card, true))}
            </div>
          </div>

          <SectionDivider />

          {showSeasonSummary ? (
            <div data-testid="season-strip" className="flex min-w-[380px] flex-[0_1_420px] flex-col gap-[8px]">
              <div className={SECTION_HEADER_CLASS}>
                <SeasonTableIcon size={18} className="shrink-0 text-bloom-green" />
                <span className={`shrink-0 whitespace-nowrap ${SECTION_TITLE_CLASS}`}>
                  {t('map.scorecards.seasonSummary', 'Season summary')}
                </span>
                <div className="ml-auto flex shrink-0 flex-nowrap items-center justify-end gap-[6px]">
                  {seasonChromeAllowed && !seasonUnsupported ? (
                    <button
                      type="button"
                      data-testid="season-view-all"
                      onClick={() => setSeasonSummaryOpen(true)}
                      className="inline-flex h-[28px] shrink-0 items-center whitespace-nowrap gap-1 rounded-[6px] border border-[#E8EBE9] bg-white px-2 py-0 font-['Inter'] text-[11px] leading-[16px] font-semibold text-[#282A29] hover:border-bloom-green/60 dark:border-[#30363d] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]"
                    >
                      <ChevronDown size={14} className="text-bloom-green" />
                      {t('map.scorecards.viewAll', 'View all')}
                    </button>
                  ) : null}
                  {seasonLocked ? (
                    <LockedDownload
                      label={downloadShortLabel}
                      hint={lockedHint}
                      className="inline-flex h-[28px] min-w-[112px] shrink-0 cursor-not-allowed items-center justify-center gap-1 whitespace-nowrap rounded-[6px] border border-[#E8EBE9] bg-white px-2 py-0 font-['Inter'] text-[11px] font-semibold leading-[16px] text-[#282A29] opacity-50 dark:border-[#30363d] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]"
                    />
                  ) : null}
                  {canDownloadSeasonPdf && !seasonUnsupported ? downloadStatus : null}
                  {canDownloadSeasonPdf && !seasonUnsupported ? (
                    <button
                      type="button"
                      data-testid="season-download"
                      onClick={onDownloadReport}
                      disabled={downloadDisabled}
                      aria-disabled={pdfBusy || undefined}
                      title={pdfDisabledTitle || downloadLongLabel}
                      aria-label={downloadLongLabel}
                      className="inline-flex h-[28px] min-w-[112px] shrink-0 justify-center items-center whitespace-nowrap gap-1 rounded-[6px] border border-[#E8EBE9] bg-white px-2 py-0 font-['Inter'] text-[11px] leading-[16px] font-semibold text-[#282A29] hover:border-bloom-green/60 disabled:opacity-50 dark:border-[#30363d] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]"
                    >
                      {downloadIcon}
                      {downloadText}
                    </button>
                  ) : null}
                </div>
              </div>
              <div data-testid="season-cards" className="grid grid-cols-[repeat(3,minmax(0,1fr))] items-stretch gap-[6px]">
                {seasonLoading ? [0, 1, 2].map((i) => <SkeletonCard key={i} />) : null}
                {seasonMessage ? (
                  <div data-testid="season-message" role={seasonLoading ? 'status' : undefined} className={`font-['Inter'] col-span-full self-center break-words text-[12px] text-[#828382]${seasonLoading ? ' text-center' : ''}`}>
                    {seasonMessage}
                  </div>
                ) : (
                  topVarieties.map((v, i) => (
                    <StatCard
                      key={varietyKey(v, i)}
                      label={varietyLabel(v, i)}
                      value={varietyValue(v)}
                      title={seasonLocked ? lockedHint : undefined}
                      isActive={false}
                      onClick={() => setSeasonSummaryOpen(true)}
                    />
                  ))
                )}
              </div>
              {renderUpgradeLine("font-['Inter'] break-words text-[11px] text-[#5d625f] dark:text-[#a8b0ab]")}
              {pdfErr ? (
                <div role={pdfErrIsInfo ? 'status' : undefined} className={`font-['Inter'] break-words text-[11px] ${pdfErrIsInfo ? 'text-[#6B7280] dark:text-gray-400' : 'text-[#EF4444]'}`}>{pdfErr}</div>
              ) : null}
            </div>
          ) : showTopImporters ? (
            <div className="flex min-w-0 flex-[0_1_300px] flex-col gap-[8px]">
              <div className={SECTION_HEADER_CLASS}>
                <Package size={18} className="shrink-0 text-bloom-green" />
                <span className={`shrink-0 whitespace-nowrap ${SECTION_TITLE_CLASS}`}>
                  {t('map.scorecards.topImporters', 'Top importers')}
                </span>
                <div className="ml-auto flex shrink-0 flex-nowrap items-center justify-end gap-[6px]">
                  <button
                    type="button"
                    onClick={() => setViewAllOpen(true)}
                    className="inline-flex items-center gap-1 rounded-[6px] border border-[#E8EBE9] bg-white px-2 py-1 font-['Inter'] text-[11px] font-semibold text-[#282A29] hover:border-bloom-green/60 dark:border-[#30363d] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]"
                  >
                    {t('map.scorecards.viewAllDaily', 'View all (daily)')}
                  </button>
                </div>
              </div>
              <TopImportersModal
                isOpen={viewAllOpen}
                onClose={() => setViewAllOpen(false)}
                selectedCommodity={seasonCommodity}
              />
              <div className="grid grid-cols-3 items-stretch gap-[6px]">
                {topImporters.length === 0 ? (
                  <div className="font-['Inter'] col-span-full self-center text-[12px] text-[#828382]">
                    {t('map.scorecards.noTopImporters', 'No importer data')}
                  </div>
                ) : (
                  topImporters.slice(0, 3).map((row, i) => (
                    <StatCard
                      key={row.importer || i}
                      label={`#${i + 1} ${row.importer || '—'}`}
                      value={fmtWeightCompact(Number(row.weight_kg) || 0)}
                      title={fmtWeight(Number(row.weight_kg) || 0)}
                      isActive={false}
                    />
                  ))
                )}
              </div>
            </div>
          ) : (
            alertsSection
          )}
          {showSeasonSummary ? (
            <>
              <SectionDivider />
              {alertsSection}
            </>
          ) : null}
        </div>
      </div>

      {/* One Season Summary modal for both layouts. Portaled to body so it
          escapes this bar's z-[1250] stacking context and paints above the
          filter strip (z-[1300]). */}
      {showSeasonSummary && typeof document !== 'undefined'
        ? createPortal(
            <SeasonSummaryModal
              isOpen={seasonSummaryOpen}
              onClose={() => setSeasonSummaryOpen(false)}
              canDownloadSeasonPdf={canDownloadSeasonPdf}
              selectedCommodity={seasonCommodity}
              email={email}
              destSel={destSel}
              originSel={originSel}
              seasonBundle={mergedBundle}
              seasonBundleLoading={seasonBundleLoading}
              seasonBundleError={seasonBundleError}
              liveStatus={live.status}
            />,
            document.body,
          )
        : null}
    </>
  );
};

export default ScorecardsBar;
